import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useState, useCallback } from "react";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useRemoveWorkspaceEmailAlias } from "@api/workspaceEmailAlias.api";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Menu } from "@design-system/Menu/Menu.lib";
import type { WorkspaceEmailAlias } from "@common/schemas/workspace-email-alias.schema";

export const EmailAliasActionsCell = memo(
    <
        T extends Pick<WorkspaceEmailAlias, "emailLocalPart" | "kind"> & {
            isDefault?: boolean;
        },
    >({
        row,
    }: CellContext<ViewTableFeatures, T, any>) => {
        const alias = row.original;
        const [showConfirmation, setShowConfirmation] = useState(false);
        const removeAlias = useRemoveWorkspaceEmailAlias();

        const handleRemoveClick = useCallback(() => {
            setShowConfirmation(true);
        }, []);

        const confirmRemove = useCallback(() => {
            removeAlias.mutate({
                emailLocalPart: alias.emailLocalPart,
                kind: alias.kind,
            });
            setShowConfirmation(false);
        }, [removeAlias, alias.emailLocalPart, alias.kind]);

        if (alias.isDefault) {
            return null;
        }

        return (
            <ViewTableCell captureClicks>
                <Menu.More>
                    <Menu.Item destructive onClick={handleRemoveClick}>
                        Remove alias
                    </Menu.Item>
                </Menu.More>

                <ConfirmationModal
                    show={showConfirmation}
                    onHide={() => setShowConfirmation(false)}
                    closeButton
                    title="Remove email alias?"
                    question={`Are you sure you want to remove the alias "${alias.emailLocalPart}"?`}
                    onConfirm={confirmRemove}
                    onReject={() => setShowConfirmation(false)}
                    yes="Remove"
                    yesBtnVariant="destructive"
                    no="Cancel"
                />
            </ViewTableCell>
        );
    },
);
